import { faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo } from "react";
import { Alert, Button, Container, Stack } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { setAccessDeniedContext, updateUserStateAtomToLocalStorage } from "../../atoms";
import type { AccessDeniedContext } from "@/utils/access/accessDeniedTypes";
import { resolveAccessDeniedPresentation } from "@/utils/access/classifyDenial";

type LocationState = {
    accessDenied?: AccessDeniedContext;
};

export default function AccessDeniedPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const state = (location.state as LocationState | null)?.accessDenied;

    useEffect(() => {
        updateUserStateAtomToLocalStorage();
        setAccessDeniedContext(null);
    }, []);

    const presentation = useMemo(
        () =>
            resolveAccessDeniedPresentation({
                source: state?.source ?? "route",
                resourceSubtype: state?.resourceSubtype,
                code: state?.code,
                message: state?.message,
                resourceLabel: state?.resourceLabel,
                requiredRoles: state?.requiredRoles,
            }),
        [state]
    );

    const title = presentation.title;
    const showRoles =
        state?.source === "route" &&
        state?.requiredRoles &&
        state.requiredRoles.length > 0 &&
        state?.userRoles &&
        state.userRoles.length > 0;

    const fallbackRoute = state?.fallbackRoute ?? "/dashboard";

    return (
        <Container fluid className="py-5">
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <Stack gap={4} className="mx-auto" style={{ maxWidth: 560 }}>
                <div className="text-center text-muted">
                    <FontAwesomeIcon icon={faLock} size="4x" aria-hidden />
                </div>
                <h1 className="h3 text-center mb-0">{title}</h1>
                <Alert variant={presentation.severity === "danger" ? "danger" : "warning"} className="mb-0">
                    <p className="mb-2">{presentation.explanation}</p>
                    <p className="mb-0 small">{presentation.nextStep}</p>
                </Alert>
                {state?.resourceLabel && (
                    <p className="text-muted text-center mb-0 small">
                        You tried to open: <strong>{state.resourceLabel}</strong>
                    </p>
                )}
                {showRoles && (
                    <div className="small text-muted text-center">
                        <div>
                            Your role: {state!.userRoles!.join(", ")}
                        </div>
                        <div>
                            Required: {state!.requiredRoles!.join(" or ")}
                        </div>
                    </div>
                )}
                <Stack direction="horizontal" gap={2} className="justify-content-center flex-wrap">
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        Back
                    </Button>
                    <Button variant="primary" as={Link} to="/dashboard">
                        Go to dashboard
                    </Button>
                    {fallbackRoute !== "/dashboard" && (
                        <Button variant="outline-primary" as={Link} to={fallbackRoute}>
                            Return to list
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Container>
    );
}
